import {
    MultiSlider,
    Text,
    TextTag,
    TokenTextAppearance,
    TokenTextColor,
} from '@volkswagen-onehub/components-core'
import React, {useEffect} from 'react'
import styled from 'styled-components'
import {useDispatch, useSelector} from 'react-redux'
import {AppDispatch} from '../thunk'
import {selectTracking, trackingData} from '../../shared/trackingServiceHelper'
import {RootState} from '../rootReducer'
import {getLabel, setUrlParams} from '../../shared/utilities'
import {useFeatureServices} from '../../use-feature-services'
import {useTranslation} from 'react-i18next'

const FilterLabel = styled(Text)`
    padding-bottom: ${(props) => props.theme.size.static200};
`

const SliderContainer = styled.fieldset`
    border: 0;
    display: flex;
    flex-direction: column;
    margin: 0;
    padding: 0;
`

interface SliderProps {
    title: string
    min: number
    max: number
    step: number
    minRangeBetweenThumbs: number
    thumbs: any
    highlights: any
    setElement: any
    sliderKey: string
}

export function FilterSlider(props: SliderProps): React.JSX.Element {
    const {t} = useTranslation('fa-flotte')
    const dispatch = useDispatch() as AppDispatch
    const {'s2:history': historyService} = useFeatureServices() as any

    const setSliderValues = (values: number | number[]): number[] => {
        const sliderValues: number[] = []

        if (typeof values === 'number') {
            return [values]
        }

        values.forEach((value: number) => {
            switch (value) {
                case Number(t('filters.slider.steps.min')):
                    sliderValues.push(Number(t('filters.slider.values.min')))
                    break
                case Number(t('filters.slider.steps.step1')):
                    sliderValues.push(Number(t('filters.slider.values.300')))
                    break
                case Number(t('filters.slider.steps.step2')):
                    sliderValues.push(Number(t('filters.slider.values.400')))
                    break
                case Number(t('filters.slider.steps.step3')):
                    sliderValues.push(Number(t('filters.slider.values.500')))
                    break
                case Number(t('filters.slider.steps.max')):
                    sliderValues.push(Number(t('filters.slider.values.max')))
            }
        })

        return sliderValues
    }

    const getValue = (value: number | number[]) =>
        typeof value === 'number'
            ? dispatch(props.setElement(setSliderValues([value])))
            : dispatch(props.setElement(setSliderValues(value)))

    const reduxValue = useSelector(
        (state: RootState) => state.filterValues[props.sliderKey]
    )

    const selectRange = (value: number | number[]) => {
        getValue(value)
        selectTracking(
            props.title,
            trackingData.MULTI_VALUE,
            setSliderValues(value)
        )
    }

    const getStepLabel = (value: number) => {
        const highlight = props.highlights.find(
            (item: {value: string; label: string}) =>
                Number(item.value) === value
        )

        return highlight ? highlight.label : String(value)
    }
    const thumbsKey = props.thumbs
        .map((thumb: {defaultValue: number}) => thumb.defaultValue)
        .join('|')

    useEffect(() => {
        const reduxValueAsString = reduxValue.join('-')
        setUrlParams(historyService, getLabel(props.title), reduxValueAsString)
    }, [reduxValue])

    return (
        <SliderContainer>
            <FilterLabel
                tag={TextTag.legend}
                appearance={TokenTextAppearance.headline200}
                color={TokenTextColor.primary}
                bold
                id={props.title}
            >
                {props.title}
            </FilterLabel>

            <MultiSlider
                key={`${props.sliderKey}-${thumbsKey}`}
                rigidThumbs
                min={props.min}
                max={props.max}
                step={props.step}
                getValueText={getStepLabel}
                minRangeBetweenThumbs={props.minRangeBetweenThumbs}
                hideValueBubbles
                showIntervals
                thumbs={props.thumbs}
                highlights={props.highlights}
                onChange={(value: number | number[]) => {
                    selectRange(value)
                }}
            />
        </SliderContainer>
    )
}
